<template>
  <div class="grid-tabs">
    <!-- === Video Wall Tab -->
    <b-modal v-model="modalShow">
      <template #modal-header>
        <h5>{{ $t("video_wall.video_wall_users") }}</h5>
      </template>
      <b-table
        class="mb-0 alarms-widget__data-table"
        :items="userVideoWall"
        :fields="fields"
        primary-key="id"
        :busy="busy"
        show-empty
        small
        :empty-text="$t('grid.widgets.alarms.table.no_data')"
        responsive
      >
        <template #cell(viewname)="row">
          <b-overlay :show="disableButton" rounded="sm">
            <b-container class="bv-example-row">
              <b-button
                variant="outline-primary"
                @click="saveTabVideoWall(row)"
                class="btn-sm"
              >
                <feather-icon
                  icon="AirplayIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </b-button>
            </b-container>
          </b-overlay>
        </template>
      </b-table>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="danger" @click="modalShow = false">
          Cancel
        </b-button>
      </template>
    </b-modal>
    <!-- Video Wall Tab === -->

    <b-tabs ref="tabs" nav-class="mb-0">
      <b-tab
        v-for="(tab, tabId) in tabs"
        :button-id="`tab_${tabId}`"
        :key="tabId"
        @click="setActiveTab(tabId)"
        :active="tabId === activeTabId"
        title-link-class="d-flex"
        :style="tabId === activeTabId && tab && tab.color ? `background: ${tab.color}` : ``"
        :title-link-attributes="{
          style: tabId === activeTabId && tab && tab.color ? `background: ${tab.color}; color: white;` : ``,
        }"
        :tab-height="tabHeight"
        class="align-items-center h-100"
        ref="tabRef"
      >
        <template #title>
          <span
            v-if="!(editTab && tabId === activeTabId)"
            @contextmenu.prevent="(event) => openContextMenu(event, tabId)"
          >
            {{ resolveTabName(tab) }}
          </span>
          <div v-else class="input-tab-name-placeholder">
            <input
              ref="tabNameInput"
              type="text"
              v-model="editingTabName"
              name="tab_name"
              id="input_tab_name"
              :placeholder="`${$tc('alarm.title', 1)} 1`"
              @keyup.enter="editTabName($event, activeTabId)"
              @blur="handleInputBlur($event, activeTabId)"
              @keydown.escape="cancelEditTabName"
              @keydown.space.prevent="handleSpace($event)"
              class="edit-tab-input"
            />
          </div>
          <span
            class="d-flex align-items-center cursor-pointer btn-remove-tab"
            @click="removeTab(tabId)"
          >
            <feather-icon
              class="my-auto"
              icon="XIcon"
              size="14"
              v-show="!isUserVideoWall"
            />
          </span>
        </template>
        <main-layout
          :tabId="tabId"
          :key="`tab-${tabId}`"
          v-show="tabId === activeTabId"
          :isActive="tabId === activeTabId"
          :tabHeight="tabHeight"
        />
      </b-tab>

      <template #tabs-end>
        <button
          class="btn btn-sm btn-icon btn-add-tab tab-add"
          v-if="tabsQty < $store.getters['app/maxTabs']"
          @click="addNewTab()"
          v-show="!isUserVideoWall"
        >
          <template v-if="tabsQty === 0">
            <span class="fs-2 mr-25">{{ $t("grid.tabs.new_tab") }}</span>
            <feather-icon icon="PlusIcon" size="14" />
          </template>
          <feather-icon
            icon="PlusIcon"
            size="14"
            v-else
            v-b-tooltip.hover.viewport="$t('tooltips.add_new_tab')"
          />
        </button>
        <button class="btn btn-sm btn-icon btn-fullscreen" @click="goFullScreen">
          <feather-icon
            icon="MinimizeIcon"
            size="18"
            v-if="fullScreenEnabled"
            v-b-tooltip.hover.viewport="$t('tooltips.minimize')"
          />
          <feather-icon
            icon="MaximizeIcon"
            size="18"
            v-else
            v-b-tooltip.hover.button.viewport="$t('tooltips.fullscreen')"
          />
        </button>
      </template>
    </b-tabs>
    <vue-context ref="menu" class="option-menu">
      <li>
        <a href="#" @click.prevent="saveTab" class="flex items-center text-sm">
          <span v-if="activeTab && activeTab.type === 'view'">
            {{ $t("grid.tabs.context_menu.update_view") }}
          </span>
          <span v-else> {{ $t("grid.tabs.context_menu.save_as_view") }} </span>
        </a>
      </li>
      <li>
        <a href="#" @click.prevent="showEditName" class="flex items-center text-sm">
          <span>{{ $t("grid.tabs.context_menu.update_name") }}</span>
        </a>
      </li>
      <li v-if="$can('update', 'VideoWall')">
        <a href="#" @click="modalShow = !modalShow" class="flex items-center text-sm">
          <span>{{ $t("video_wall.assign_a_video_wall") }}</span>
        </a>
      </li>
    </vue-context>
  </div>
</template>

<script>
import store from "@/store";
import MainLayout from "./layouts/MainLayout.vue";
import { BTabs, BTab, BButton } from "bootstrap-vue";
import VueContext from "vue-context";
import axios from "@axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { debounce } from "lodash";
import { useLogger } from "@/composables/logs";
import { LOG_LEVELS, LOG_MESSAGE_CODES } from "@/utils/logCatalog";

export default {
  components: {
    BTabs,
    BTab,
    MainLayout,
    VueContext,
    BButton,
    ToastificationContent,
  },

  data() {
    return {
      editTab: false,
      modalShow: false,
      editingTabName: "",
      busy: false,
      fields: [
        {
          key: "name",
          label: this.$t("commands.gridWidget.table.rows.1"),
          minSize: 300,
        },
        {
          key: "viewname",
          label: "",
          minSize: 300,
        },
      ],
      disableButton: false,
      tabRefHeight: 0,
      fullscreenChangeHandler: null,
      windowResizeHandler: null,
      tabsResizeObserver: null,
      previousVideoTourTabs: [],
      tabsInitialized: false,
      restoredLogged: false,
      previousTabsSnapshot: {},
    };
  },
  created() {
    this.debouncedUpdateTabRefHeight = debounce(this.updateTabRefHeight, 100);

    const { logView, logAction } = useLogger();
    this._logView = logView;
    this._logAction = logAction;
  },
  mounted() {
    this.fullscreenChangeHandler = () => {
      store.commit("grid/setFullScreen", !!document.fullscreenElement);
      this.debouncedUpdateTabRefHeight();
    };

    this.windowResizeHandler = () => {
      this.debouncedUpdateTabRefHeight();
    };

    document.addEventListener("fullscreenchange", this.fullscreenChangeHandler);
    window.addEventListener("resize", this.windowResizeHandler);

    if (typeof ResizeObserver !== "undefined") {
      this.tabsResizeObserver = new ResizeObserver(() => {
        this.debouncedUpdateTabRefHeight();
      });

      if (this.$refs.tabs && this.$refs.tabs.$el) {
        this.tabsResizeObserver.observe(this.$refs.tabs.$el);
      }
    }

    this.updateTabRefHeight();

    this.$nextTick(() => {
      this.previousTabsSnapshot = JSON.parse(JSON.stringify(this.tabs));

      this.previousVideoTourTabs = Object.entries(this.tabs)
        .filter(([_, tab]) => tab.type === "video-tour")
        .map(([tabId]) => tabId);

      this.previousViewTabs = Object.entries(this.tabs)
        .filter(([_, tab]) => tab.type === "view")
        .map(([tabId]) => tabId);  

      //If user had one tab opened
      if (!this.restoredLogged && this.previousVideoTourTabs.length > 0) {
        this.previousVideoTourTabs.forEach((tabId) => {
          const tab = this.tabs[tabId];

          this._logAction({
            action: "UPDATE VIDEO-TOUR RELOAD",
            resource: "video-tour",
            level: LOG_LEVELS.INFO,
            message: LOG_LEVELS.INFO,
            description: `ya tenia abierta la vista [${tab?.name}] de video-tour`,
            path: this.$route.fullPath,
            data: {
              tabId,
              name: tab?.name,
              type: tab?.type,
              logmessage: LOG_MESSAGE_CODES.RESOURCE_UPDATED,
            },
            old_data: {},
          });
        });
      }

      if (!this.restoredLogged && this.previousViewTabs.length > 0) {
        this.previousViewTabs.forEach((tabId) => {
          const tab = this.tabs[tabId];

          this._logAction({
            action: "UPDATE VIEW RELOAD",
            resource: "view",
            level: LOG_LEVELS.INFO,
            message: LOG_LEVELS.INFO,
            description: `ya tenia abierta la vista [${tab?.name}] de vistas`,
            path: this.$route.fullPath,
            data: {
              tabId,
              name: tab?.name,
              type: tab?.type,
              logmessage: LOG_MESSAGE_CODES.RESOURCE_UPDATED,
            },
            old_data: {},
          });
        });
      }

      this.restoredLogged = true;
      this.tabsInitialized = true;
    });
  },
  updated() {
    this.debouncedUpdateTabRefHeight();

    document.addEventListener("fullscreenchange", () =>
      store.commit("grid/setFullScreen", !!document.fullscreenElement)
    );
  },
  beforeDestroy() {
    if (this.fullscreenChangeHandler) {
      document.removeEventListener("fullscreenchange", this.fullscreenChangeHandler);
    }

    if (this.windowResizeHandler) {
      window.removeEventListener("resize", this.windowResizeHandler);
    }

    if (this.tabsResizeObserver) {
      this.tabsResizeObserver.disconnect();
      this.tabsResizeObserver = null;
    }

    if (this.debouncedUpdateTabRefHeight) {
      this.debouncedUpdateTabRefHeight.cancel();
    }
  },
  computed: {
    userVideoWall() {
      return store.getters["video_wall/getItems"];
    },
    user() {
      return store.getters["user/getUser"];
    },
    isUserVideoWall() {
      return this.user.video_wall === 1;
    },
    tabs() {
      return store.state.grid.tabs;
    },
    activeTabId() {
      return store.state.grid.activeTab;
    },
    activeTab() {
      return this.tabs[this.activeTabId];
    },
    tabsQty() {
      return this.tabs ? Object.keys(this.tabs).length : 0;
    },
    fullScreenEnabled() {
      return store.state.grid.fullScreenEnabled;
    },
    sidebarWidth() {
      return store.state.grid.sidebarWidth;
    },
    isToolbarsCollapsed() {
      return store.state.psimConfig.isToolbarsCollapsed;
    },
    isAltPressed() {
      return store.state.grid.isAltPressed;
    },
    tabHeight() {
      return this.tabRefHeight;
    },
  },
  methods: {
    updateTabRefHeight(retries = 3) {
      this.$nextTick(() => {
        const tabRefs = this.$refs.tabRef;
        const tabRef = Array.isArray(tabRefs) ? tabRefs[0] : tabRefs;
        let height = tabRef && tabRef.$el ? tabRef.$el.clientHeight : 0;

        // Fallback: try getting height from .tab-content container
        if (height === 0 && this.$refs.tabs && this.$refs.tabs.$el) {
          const tabContent = this.$refs.tabs.$el.querySelector(".tab-content");
          if (tabContent) {
            height = tabContent.clientHeight;
          }
        }

        if (height > 0) {
          if (this.tabRefHeight !== height) {
            this.tabRefHeight = height;
          }
        } else if (retries > 0) {
          // Retry after a frame if height is still 0
          requestAnimationFrame(() => {
            this.updateTabRefHeight(retries - 1);
          });
        }
      });
    },
    resolveTabName(tab) {
      if (!tab) return;

      if (tab.name) {
        return tab.name;
      }

      return `Tab ${tab.order}`;
    },
    addNewTab() {
      store.dispatch("grid/addNewTab");
      this.selectLastTab();
    },
    removeTab(tabId) {
      store.dispatch("grid/removeTab", tabId);
      this.editTab = false;
      this.selectLastTab();
    },
    setActiveTab(tabId) {
      this.editTab = false;
      store.dispatch("grid/setActiveTab", tabId);
    },
    selectLastTab() {
      if (this.tabs) {
        this.setActiveTab(Object.keys(this.tabs)[Object.keys(this.tabs).length - 1]);
      }
    },
    goFullScreen() {
      if (!this.fullScreenEnabled) {
        this.$refs.tabs.$el.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    },
    openContextMenu(event, tabId) {
      this.setActiveTab(tabId);
      this.$refs.menu.open(event);
    },
    saveTab() {
      const activeTab = JSON.parse(JSON.stringify(this.activeTab));
      this.setActiveTab(this.activeTabId);
      if (activeTab.slots.length > 0) {
        activeTab.id = this.activeTabId;
        activeTab.type = "view";
        const data = {
          tabId: activeTab.id,
          name: activeTab.name ? activeTab.name : "Tab 1",
          data: activeTab,
        };
        //this.syncGridState();
        store
          .dispatch("grid/createOrUpdateView", { ...data })
          .then((result) => {
            // Si se creó un nuevo layout, mostrar mensaje
            if (result && result.newLayoutCreated) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: this.$t("layout_builder.messages.new_layout_created"),
                  icon: "InfoIcon",
                  variant: "info",
                },
              });
            }

            store.dispatch("grid/updateTabType", {
              id: activeTab.id,
              type: "view",
            });
          })
          .catch((error) => {
            console.error("Error guardando vista:", error);
          });
      }
    },
    saveTabVideoWall(row) {
      const activeTab = JSON.parse(JSON.stringify(this.activeTab));
      activeTab.id = this.activeTabId;

      const data = {
        id_user: row.item.id,
        tab: {
          tabId: this.activeTabId,
          name: activeTab.name ? activeTab.name : "Tab 1",
          data: activeTab,
        },
      };
      axios.post("v1/video-wall/push-tab-to-user", { ...data });
      setTimeout(() => {
        this.modalShow = false;
      }, 200);
    },
    showEditName() {
      this.editTab = !this.editTab;
      if (this.editTab) {
        this.editingTabName = this.activeTab.name || "";
        this.$nextTick(() => {
          if (
            Array.isArray(this.$refs.tabNameInput) &&
            this.$refs.tabNameInput.length > 0
          ) {
            this.$refs.tabNameInput[0].focus();
          } else if (this.$refs.tabNameInput) {
            this.$refs.tabNameInput.focus();
          }
        });
      }
    },
    handleSpace(event) {
      event.stopPropagation();
      this.editingTabName += " ";
    },
    handleInputBlur(event, tabId) {
      if (this.editingTabName !== this.activeTab.name) {
        this.editTabName(event, tabId);
      } else {
        this.editTab = false;
      }
    },
    cancelEditTabName() {
      this.editingTabName = this.activeTab.name || "";
      this.editTab = false;
    },
    editTabName(event, tabId) {
      if (!this.editingTabName.trim()) {
        this.editingTabName = this.activeTab.name || "";
        this.editTab = false;
        return;
      }
      const data = {
        id: tabId,
        name: this.editingTabName,
      };
      store.dispatch("grid/editTabName", { data });
      this.saveTab();
      this.editTab = false;
    },
  },

  watch: {
    tabs: {
      deep: true,
      handler(newTabs) {
        if (!this.tabsInitialized || !this.restoredLogged) return;

        const currentVideoTours = Object.entries(newTabs)
          .filter(([_, tab]) => tab.type === "video-tour")
          .map(([tabId]) => tabId);

        // If user close a videotour
        const closed = this.previousVideoTourTabs.filter(
          (id) => !currentVideoTours.includes(id)
        );

        closed.forEach((tabId) => {
          const tab = this.previousTabsSnapshot[tabId];

          //custom basepayload
          this._logAction({
            action: "UPDATE VIDEO-TOUR CLOSED",
            resource: "VIDEO-TOUR",
            level: LOG_LEVELS.INFO,
            message: LOG_LEVELS.INFO,
            description: `ha cerrado el video-tour [${tab?.name}]`,
            path: this.$route.fullPath,
            data: {
              tabId,
              name: tab?.name,
              type: tab?.type,
              logmessage: LOG_MESSAGE_CODES.RESOURCE_UPDATED,
            },
            old_data: {},
          });
        });

        //If user open a videotour
        const opened = currentVideoTours.filter(
          (id) => !this.previousVideoTourTabs.includes(id)
        );

        opened.forEach((tabId) => {
          const tab = this.tabs[tabId];

          //custom basepayload
          this._logAction({
            action: "UPDATE VIDEO-TOUR OPENED",
            resource: "VIDEO-TOUR",
            level: LOG_LEVELS.INFO,
            message: LOG_LEVELS.INFO,
            description: `ha abierto el video-tour [${tab?.name}]`,
            path: this.$route.fullPath,
            data: {
              tabId,
              name: tab?.name,
              type: tab?.type,
              logmessage: LOG_MESSAGE_CODES.RESOURCE_RETRIEVED,
            },
            old_data: {},
          });
        });

        const currentViews = Object.entries(newTabs)
        .filter(([_, tab]) => tab.type === "view")
        .map(([tabId]) => tabId);

        // If user close a view
        const closedViews = this.previousViewTabs.filter(
          (id) => !currentViews.includes(id)
        );

        closedViews.forEach((tabId) => {
          const tab = this.previousTabsSnapshot[tabId];

          //custom basepayload
          this._logAction({
            action: "UPDATE VIEW CLOSED",
            resource: "VIEW",
            level: LOG_LEVELS.INFO,
            message: LOG_LEVELS.INFO,
            description: `ha cerrado la vista [${tab?.name}]`,
            path: this.$route.fullPath,
            data: {
              tabId,
              name: tab?.name,
              type: tab?.type,
              logmessage: LOG_MESSAGE_CODES.RESOURCE_UPDATED,
            },
            old_data: {},
          });
        });

        // if user open a view
        const openedViews = currentViews.filter(
          (id) => !this.previousViewTabs.includes(id)
        );

        openedViews.forEach((tabId) => {
          const tab = this.tabs[tabId];

          //custom basepayload
          this._logAction({
            action: "UPDATE VIEW OPENED",
            resource: "VIEW",
            level: LOG_LEVELS.INFO,
            message: LOG_LEVELS.INFO,
            description: `ha abierto la vista [${tab?.name}]`,
            path: this.$route.fullPath,
            data: {
              tabId,
              name: tab?.name,
              type: tab?.type,
              logmessage: LOG_MESSAGE_CODES.RESOURCE_RETRIEVED,
            },
            old_data: {},
          });
        });

        this.previousVideoTourTabs = [...currentVideoTours];


        //save last tab closed
        this.previousTabsSnapshot = JSON.parse(JSON.stringify(newTabs));
        this.previousVideoTourTabs = [...currentVideoTours];
        this.previousViewTabs = [...currentViews];

      },
    },
  },

};
</script>

<style scoped lang="scss">
@import "~vue-context/src/sass/vue-context";
</style>
